import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GraduationCap, Users, BookOpen, Settings } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../contexts";

export const Portal = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [userType, setUserType] = useState(new URLSearchParams(location.search).get("type") || "student");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "", first_name: "", last_name: "", grade: "", phone: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(formData.email, formData.password);
        toast.success("Welcome back!");
      } else {
        await register({ ...formData, user_type: userType });
        toast.success("Account created successfully!");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#002147] to-[#003366] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-[#002147] rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-2xl font-bold">YCP</span>
          </div>
          <CardTitle className="text-2xl">{mode === "login" ? "Welcome Back" : "Create Account"}</CardTitle>
          <CardDescription>{mode === "login" ? "Sign in to access your portal" : `Register as a ${userType}`}</CardDescription>
        </CardHeader>
        <CardContent>
          {mode === "register" && (
            <div className="grid grid-cols-2 gap-2 mb-6">
              <Button type="button" variant={userType === "student" ? "default" : "outline"} className={`${userType === "student" ? "bg-[#002147]" : ""}`} onClick={() => setUserType("student")} data-testid="user-type-student"><GraduationCap size={16} className="mr-1" /> Student</Button>
              <Button type="button" variant={userType === "parent" ? "default" : "outline"} className={`${userType === "parent" ? "bg-[#002147]" : ""}`} onClick={() => setUserType("parent")} data-testid="user-type-parent"><Users size={16} className="mr-1" /> Parent</Button>
              <Button type="button" variant={userType === "teacher" ? "default" : "outline"} className={`${userType === "teacher" ? "bg-[#002147]" : ""}`} onClick={() => setUserType("teacher")} data-testid="user-type-teacher"><BookOpen size={16} className="mr-1" /> Teacher</Button>
              <Button type="button" variant={userType === "admin" ? "default" : "outline"} className={`${userType === "admin" ? "bg-[#FF6B00]" : ""}`} onClick={() => setUserType("admin")} data-testid="user-type-admin"><Settings size={16} className="mr-1" /> Admin</Button>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div className="grid grid-cols-2 gap-4">
                <div><Label htmlFor="first_name">First Name</Label><Input id="first_name" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required data-testid="register-first-name" /></div>
                <div><Label htmlFor="last_name">Last Name</Label><Input id="last_name" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required data-testid="register-last-name" /></div>
              </div>
            )}
            <div><Label htmlFor="email">Email</Label><Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="auth-email" /></div>
            <div><Label htmlFor="password">Password</Label><Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required data-testid="auth-password" /></div>
            {mode === "register" && userType === "student" && (
              <div><Label htmlFor="grade">Grade</Label>
                <Select value={formData.grade} onValueChange={(v) => setFormData({ ...formData, grade: v })}>
                  <SelectTrigger data-testid="register-grade"><SelectValue placeholder="Select grade" /></SelectTrigger>
                  <SelectContent>{["9th", "10th", "11th", "12th"].map(g => (<SelectItem key={g} value={g}>{g} Grade</SelectItem>))}</SelectContent>
                </Select>
              </div>
            )}
            <Button type="submit" className="w-full bg-[#FF6B00] hover:bg-[#E65C00]" disabled={loading} data-testid="auth-submit">
              {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <button type="button" className="text-sm text-[#002147] hover:underline" onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="auth-toggle">
              {mode === "login" ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
